import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { parse } from "date-fns";
import { useSetAtom } from "jotai";
import { useFragment } from "react-relay";

import { PaymentMethod } from "@/features/Square/PaymentMethod";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { isPaymentMethodEntryModalOpenAtom } from "../../atoms/stores";
import { BookingPaymentMethodSelect_booking$key } from "./__generated__/BookingPaymentMethodSelect_booking.graphql";
import { isCompletedApplePayPayment } from "./utils/isCompletedApplePayPayment";

type BookingPaymentMethodSelectProps = {
  fragmentRef: BookingPaymentMethodSelect_booking$key | null;
};

export const BookingPaymentMethodSelect = ({
  fragmentRef,
}: BookingPaymentMethodSelectProps) => {
  const setShouldModalBeOpen = useSetAtom(isPaymentMethodEntryModalOpenAtom);

  const booking = useFragment(
    BookingPaymentMethodSelectFragmentString,
    fragmentRef
  );

  const isDraftBooking = booking?.status === "DRAFT";
  const canUpdate = booking?.canUpdate;
  const timeslot = booking?.startSlot;
  const selectedCard = booking?.paymentCardOnFile;
  const wasPaidWithApplePay = booking?.orders?.edges?.some((orderEdge) =>
    orderEdge?.node?.orderPayments?.edges?.some((paymentEdge) =>
      isCompletedApplePayPayment(
        paymentEdge?.node?.paymentSource,
        paymentEdge?.node?.status
      )
    )
  );

  const agendaDate = timeslot?.agenda.date
    ? parse(timeslot.agenda.date, "yyyy-MM-dd", new Date())
    : 0;

  const requirePaymentCardOnFile =
    booking?.paymentRequirements?.requirePaymentCardOnFile;
  const requiresPrePayment =
    booking?.paymentRequirements?.prePaymentRequirement === "FULL";

  const cardNoLongerNeeded =
    booking?.source === "STAFF" &&
    booking.paymentCardOnFile === null &&
    booking.status === "CONFIRMED";

  const shouldDisplayPaymentMethodSelector =
    (requirePaymentCardOnFile || requiresPrePayment) && !cardNoLongerNeeded;

  if (wasPaidWithApplePay) {
    return (
      <Box
        p={3}
        borderRadius="2"
        backgroundColor="surface-primary"
        borderWidth={4}
        borderColor="surface-secondary"
        gap={2}
      >
        <Text variant="mainSmallSemiBold">{t`Payment Method`}</Text>
        <VStack paddingTop={2}>
          <Text variant="mainBaseSemiBold">{t`Paid with Apple Pay`}</Text>
        </VStack>
      </Box>
    );
  }

  return shouldDisplayPaymentMethodSelector ? (
    <PaymentMethod
      squareCardRef={selectedCard}
      usageDate={agendaDate || null}
      onAddPress={() => setShouldModalBeOpen(true)}
      onChangePress={() => setShouldModalBeOpen(true)}
      active={isDraftBooking}
      canUpdate={!!canUpdate}
    />
  ) : null;
};

export const BookingPaymentMethodSelectFragmentString = graphql`
  fragment BookingPaymentMethodSelect_booking on Booking {
    paymentRequirements {
      requirePaymentCardOnFile
      prePaymentRequirement
    }
    __typename
    source
    canUpdate
    ... on StandardBooking {
      status
    }
    ... on LessonBooking {
      status
    }
    paymentCardOnFile {
      ...PaymentMethod_squareCardOnFile
    }
    orders {
      edges {
        node {
          orderPayments {
            edges {
              node {
                paymentSource
                status
              }
            }
          }
        }
      }
    }
    startSlot {
      agenda {
        date
      }
    }
  }
`;
